import { createContext } from "@pharaday/utils";
import type { ReactNode, RefObject } from "react";

import type { Command } from "../../core/domain/command";
import type { CommandContextEnum } from "../../core/domain/command-context.enum";
import type { CommandIdentifierEnum } from "../../core/domain/command-identifier.enum";
import type { Shortcut } from "../../core/domain/shortcut";

export type CommandItemProps<P = unknown> = P & Command;

/**
 * feature-lib: Define commandFn and commandItem component for a specific CommandIdentifierEnum
 */
export type CommandIdentifierHandler = {
  identifier: CommandIdentifierEnum;
  commandFn: () => void;
  commandItem: (props: CommandItemProps) => ReactNode;
};

/**
 * backend: Define shortcut to trigger command for a specific CommandIdentifierEnum
 */
export type CommandIdentifierShortcut = {
  identifier: CommandIdentifierEnum;
  shortcut: Shortcut;
};

export type CommandPaletteInstance = {
  context?: CommandContextEnum;
  header?: ReactNode;
  isReady: boolean;
};

export type CommandPaletteContext = {
  /**
   * A map that store CommandIdentifierHandler registered from feature-lib
   */
  commandHandler: RefObject<Map<CommandIdentifierEnum, CommandIdentifierHandler>>;
  /**
   * A map that store enabled CommandIdentifierShortcut fetched from backend
   */
  enabledCommandShortcut: RefObject<Map<CommandIdentifierEnum, CommandIdentifierShortcut>>;
  /**
   * An instance that store status of CommandPalette
   */
  instance: CommandPaletteInstance;
  /**
   * Switch the command context of the instance
   */
  switchContext: (commandContext: CommandContextEnum, header?: ReactNode) => void;
  /**
   * Disabled the CommandPalette
   */
  disabled: boolean;
};

export const [useCommandPaletteContext, CommandPaletteContextProvider] = createContext<CommandPaletteContext>();
